import React from 'react';

// Componente de redirección inteligente para la ruta raíz
export const RootRedirect = React.lazy(
  () => import('@/shared/components/RootRedirect').then(m => ({ default: m.default }))
);

// Lazy loading de páginas públicas (mantenidas para rutas específicas)
export const PublicPropertiesPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.PropertiesPage }))
);
export const PropertyDetailPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.PropertyDetailPage }))
);
export const ContactPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.ContactPage }))
);
// Task #664 — Public plans catalog (route /pricing)
export const PlansCatalogPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.PlansCatalogPage }))
);
// Task #673 — Public consolidated franchise landing (route /n/:slug)
export const NetworkLandingPage = React.lazy(() =>
  import('@/features/network-public').then(m => ({ default: m.NetworkLandingPage }))
);

// Lazy loading de páginas de autenticación
export const LoginPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.LoginPage }))
);
export const RegisterPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.RegisterPage }))
);
// TASK #665 — Signup wizard multi-step (country detect + currency + trial)
export const SignupPage = React.lazy(() =>
  import('@/features/onboarding').then(m => ({ default: m.SignupPage }))
);
export const ForgotPasswordPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.ForgotPasswordPage }))
);
export const EmailVerificationPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.EmailVerificationPage }))
);
// GH#1826 — página pública de aceptación de invitación de agente (/aceptar-invitacion?token=)
export const AcceptInvitationPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.AcceptInvitationPage }))
);
// GH#1797 (a) — estado público de un pedido de borrado de datos de Meta (/borrado-datos?code=)
export const MetaDataDeletionStatusPage = React.lazy(() =>
  import('@/features/meta-data-deletion').then(m => ({ default: m.MetaDataDeletionStatusPage }))
);
// #1062 — la que consume el enlace del correo (/verificar-email?token=…).
// Distinta de EmailVerificationPage, que es "revisa tu bandeja" y pide sesión.
export const VerifyEmailPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.VerifyEmailPage }))
);
export const OnboardingPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.OnboardingPage }))
);
export const AdminLoginPage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.AdminLoginPage }))
);
export const ProfilePage = React.lazy(() =>
  import('@/features/security').then(m => ({ default: m.ProfilePage }))
);

// Lazy loading de páginas del módulo de agencias
export const AgencyDashboardPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AgencyDashboardPage }))
);
// #790 #792 — Theme Options (Apariencia)
export const AgencyThemeOptionsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.ThemeOptionsPage }))
);
export const InventoryPage = React.lazy(() =>
  import('@/features/inventory').then(m => ({ default: m.InventoryPage }))
);
export const SharedPropertiesPage = React.lazy(() =>
  import('@/features/properties-shared').then(m => ({ default: m.PropertiesPage }))
);
export const CreatePropertyPage = React.lazy(() =>
  import('@/features/properties-shared').then(m => ({ default: m.CreatePropertyPage }))
);
export const AgencyEditPropertyPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.EditPropertyPage }))
);
// Agents shared pages - usado por agency y broker
export const SharedAgentsPage = React.lazy(() =>
  import('@/features/agents-shared').then(m => ({ default: m.AgentsPage }))
);
export const AgencyAddAgentPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AddAgentPage }))
);
export const AgencyEditAgentPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.EditAgentPage }))
);
// CreateClientPage ahora está en clients-shared (ver más abajo)
export const EditClientPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.EditClientPage }))
);
export const AgencyQuotesPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AgencyQuotesPage }))
);
export const AgencyCrmPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.CrmPage }))
);
// LB-12 Builder A — Lead detail page
export const AgencyCrmLeadDetailPage = React.lazy(
  () => import('@/features/crm/pages/LeadDetailPage')
);
// LB-12 Builder B (2026-06-03) — Lead list page con Kanban + BulkToolbar
export const AgencyCrmLeadsListPage = React.lazy(
  () => import('@/features/crm/pages/CrmLeadsListPage')
);
// GHL 7.7 — campos personalizados de leads
export const AgencyCrmCamposPage = React.lazy(
  () => import('@/features/crm/pages/CamposPersonalizadosPage')
);
export const AgencyProjectsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.ProjectsPage }))
);
export const AgencyProjectDetailPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.ProjectDetailPage }))
);
export const AgencyCreateProjectPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.CreateProjectPage }))
);
export const AgencyErpPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.ErpPage }))
);
export const AgencyCreateInvoicePage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AgencyCreateInvoicePage }))
);
export const AgencyDocumentsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.DocumentsPage }))
);
export const AgencyCreateDocumentPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.CreateDocumentPage }))
);
export const DgiiPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.DgiiPage }))
);

// Admin UI P1 (Gabriel backlog) — Subscriptions / AuditEvents / ErpMetrics
export const AdminSubscriptionsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AdminSubscriptionsPage }))
);
export const AdminAuditEventsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AdminAuditEventsPage }))
);
export const AdminErpMetricsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AdminErpMetricsPage }))
);
// DEPRECATED: Páginas fusionadas en ERP - ahora son redirects
export const AgencyFinancialPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.FinancialPage }))
);
export const AgencyRegisterIncomePage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.RegisterIncomePage }))
);
export const AgencyMarketingPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingPage }))
);
export const AgencyCreateCampaignPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.CreateCampaignPage }))
);
export const MetaAdsCampaignBuilderPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MetaAdsCampaignBuilderPage }))
);
export const AgencyMarketingPublicationsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingPublicationsPage }))
);
export const AgencyPublicacionDetallePage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.PublicacionDetallePage }))
);
export const AgencyMarketingPostComposerPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingPostComposerPage }))
);
export const AgencyMarketingAdsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingAdsPage }))
);
export const AgencyMarketingAutomationPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingAutomationPage }))
);
export const AgencyMarketingMarketplacePage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingMarketplacePage }))
);
export const AgencyMarketingEmailPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingEmailPage }))
);
export const AgencyMarketingSeoPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.MarketingSeoPage }))
);
// #870 — gestion de embudos (pipelines) configurables
export const AgencyPipelinesPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.PipelinesPage }))
);

// #882 — plantillas de WhatsApp (tenant las crea y las manda a review de Meta)
export const WhatsappTemplatesPage = React.lazy(() =>
  import('@/features/whatsapp-templates').then(m => ({ default: m.WhatsappTemplatesPage }))
);

export const AgencyReportsPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.ReportsPage }))
);
export const AgencyAtribucionPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AtribucionPage }))
);
export const AgencyCreateReportPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.CreateReportPage }))
);
export const AgencySettingsPage = React.lazy(() =>
  import('@/features/settings-shared').then(m => ({ default: m.SettingsPage }))
);
// #875 — la pagina donde el CLIENTE de la agencia paga. Publica, sin login.
export const PublicPayPage = React.lazy(() =>
  import('@/features/payment-links').then(m => ({ default: m.PublicPayPage }))
);
// #876 — Publicidad: gasto, leads y costo por lead de las campañas de Meta.
export const AdsPage = React.lazy(() =>
  import('@/features/ads').then(m => ({ default: m.AdsPage }))
);
export const AgencyPlansPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.AgencyPlansPage }))
);
// Task #666 — Mi Suscripción (tenant-self billing dashboard)
export const MySubscriptionPage = React.lazy(() =>
  import('@/features/billing').then(m => ({ default: m.MySubscriptionPage }))
);
// Tienda de addons (solo lectura; JANUS pmo #1522)
export const AddonsStorePage = React.lazy(() =>
  import('@/features/billing').then(m => ({ default: m.AddonsStorePage }))
);
export const AgencyTasksPage = React.lazy(() =>
  import('@/features/agency').then(m => ({ default: m.TasksPage }))
);

// Lazy loading de páginas del módulo de promotora
export const PromotoraDashboardPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({
    default: m.PromotoraDashboardPage,
  }))
);
export const PromotoraProjectsPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.ProjectsPage }))
);
export const PromotoraUnitsPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.UnitsPage }))
);
export const PromotoraLeadsPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.LeadsPage }))
);
export const PromotoraAgenciesPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.AgenciesPage }))
);
export const PromotoraBillingPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.BillingPage }))
);
export const PromotoraReportsPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.ReportsPage }))
);
export const PromotoraSettingsPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.SettingsPage }))
);
// GH#2250 — planes de la promotora (antes «cambiar plan» la mandaba a Mi suscripción)
export const PromotoraPlansPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.PlansPage }))
);

// Formularios de creación del módulo promotora
export const PromotoraNewProjectPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.NewProjectPage }))
);
export const PromotoraNewUnitPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.NewUnitPage }))
);
export const PromotoraNewLeadPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.NewLeadPage }))
);
export const PromotoraNewAgencyPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.NewAgencyPage }))
);
export const PromotoraNewInvoicePage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.NewInvoicePage }))
);

// Formularios de edición del módulo promotora
export const PromotoraEditProjectPage = React.lazy(() =>
  import('@/features/promotora').then(m => ({ default: m.EditProjectPage }))
);

// Lazy loading de páginas del módulo broker
export const BrokerDashboardPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerDashboardPage }))
);
// BrokerAgentsPage ahora usa SharedAgentsPage (definido arriba)
// Clients shared pages - usado por agency, broker y agente
export const SharedClientsPage = React.lazy(() =>
  import('@/features/clients-shared').then(m => ({ default: m.ClientsPage }))
);

export const CreateClientPage = React.lazy(() =>
  import('@/features/clients-shared').then(m => ({ default: m.CreateClientPage }))
);

export const ClientsTrashPage = React.lazy(() =>
  import('@/features/clients-shared').then(m => ({ default: m.ClientsTrashPage }))
);

// Mantener BrokerClientsPage por compatibilidad (puede ser eliminado después)
export const BrokerClientsPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerClientsPage }))
);
export const BrokerCommissionsPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerCommissionsPage }))
);
export const BrokerCommissionApprovalPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerCommissionApprovalPage }))
);
export const BrokerReportsPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerReportsPage }))
);
export const BrokerSettingsPage = React.lazy(() =>
  import('@/features/settings-shared').then(m => ({ default: m.SettingsPage }))
);
export const BrokerGoalsPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerGoalsPage }))
);
export const BrokerPlansPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerPlansPage }))
);
export const BrokerAgendaPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerAgendaPage }))
);
export const BrokerDocumentsPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerDocumentsPage }))
);
export const BrokerCreateLeadPage = React.lazy(() =>
  import('@/features/broker').then(m => ({ default: m.BrokerCreateLeadPage }))
);

// Lazy loading de páginas del módulo agente
export const AgenteDashboardPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteDashboardPage }))
);
export const AgentePropertiesPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgentePropertiesPage }))
);
export const AgenteCreatePropertyPage = React.lazy(() =>
  import('@/features/agente').then(m => ({
    default: m.AgenteCreatePropertyPage,
  }))
);
export const AgentePropertyDetailPage = React.lazy(() =>
  import('@/features/agente').then(m => ({
    default: m.AgentePropertyDetailPage,
  }))
);
export const AgenteAddClientPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteAddClientPage }))
);
export const AgenteQuotesPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteQuotesPage }))
);
export const AgenteCreateQuotePage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteCreateQuotePage }))
);
export const AgenteEditQuotePage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteEditQuotePage }))
);
export const AgenteAddAppointmentPage = React.lazy(() =>
  import('@/features/agente').then(m => ({
    default: m.AgenteAddAppointmentPage,
  }))
);
export const AgenteAppointmentsPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteAppointmentsPage }))
);
export const AgenteSalesPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteSalesPage }))
);
export const AgenteRegisterSalePage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteRegisterSalePage }))
);
export const AgenteReportsPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteReportsPage }))
);
export const AgenteSettingsPage = React.lazy(() =>
  import('@/features/agente').then(m => ({ default: m.AgenteSettingsPage }))
);

// Lazy loading de páginas del módulo legal
export const LegalDashboardPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalDashboardPage }))
);
export const LegalContractsPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalContractsPage }))
);
export const LegalCreateContractPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalCreateContractPage }))
);
export const LegalTemplatesPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalTemplatesPage }))
);
export const LegalCreateTemplatePage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalCreateTemplatePage }))
);
export const LegalAuditPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalAuditPage }))
);
export const LegalCreateAuditPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalCreateAuditPage }))
);
export const LegalSettingsPage = React.lazy(() =>
  import('@/features/legal').then(m => ({ default: m.LegalSettingsPage }))
);

// Lazy loading de páginas del módulo contable
export const AccountingDashboardPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingDashboardPage,
  }))
);
export const AccountingInvoicesPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingInvoicesPage,
  }))
);
export const AccountingCreateInvoicePage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingCreateInvoicePage,
  }))
);
export const AccountingPaymentsPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingPaymentsPage,
  }))
);
export const AccountingRegisterPaymentPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingRegisterPaymentPage,
  }))
);
export const AccountingReportsPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingReportsPage,
  }))
);
export const AccountingSettingsPage = React.lazy(() =>
  import('@/features/accounting').then(m => ({
    default: m.AccountingSettingsPage,
  }))
);

// Lazy loading de páginas del módulo operativo
export const OperationalDashboardPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalDashboardPage,
  }))
);
export const OperationalMaintenancePage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalMaintenancePage,
  }))
);
export const OperationalCleaningPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalCleaningPage,
  }))
);
export const OperationalCheckinPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalCheckinPage,
  }))
);
export const OperationalIncidentsPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalIncidentsPage,
  }))
);
export const OperationalReportIncidentPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalReportIncidentPage,
  }))
);
export const OperationalNewCheckinPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalNewCheckinPage,
  }))
);
export const OperationalNewCleaningPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalNewCleaningPage,
  }))
);
export const OperationalNewMaintenancePage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalNewMaintenancePage,
  }))
);
export const OperationalSettingsPage = React.lazy(() =>
  import('@/features/operational').then(m => ({
    default: m.OperationalSettingsPage,
  }))
);

// Lazy loading de páginas del módulo cliente
export const ClientDashboardPage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientDashboardPage }))
);
export const ClientPropertiesPage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientPropertiesPage }))
);
export const ClientFavoritesPage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientFavoritesPage }))
);
export const ClientAppointmentsPage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientAppointmentsPage }))
);
export const ClientNewAppointmentPage = React.lazy(() =>
  import('@/features/client').then(m => ({
    default: m.ClientNewAppointmentPage,
  }))
);
export const ClientProfilePage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientProfilePage }))
);
// Sprint 10 — Modelo C Híbrido: "Mis agencias" (buyer global controla sus vínculos)
export const ClientMisAgenciasPage = React.lazy(
  () => import('@/features/buyer/pages/MisAgenciasPage')
);
// #790 #10 — "Mis búsquedas guardadas" (alertas de propiedad del buyer)
export const ClientSavedSearchesPage = React.lazy(() =>
  import('@/features/client').then(m => ({ default: m.ClientSavedSearchesPage }))
);

// Lazy loading de páginas del módulo condominio
export const CondominiumDashboardPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumDashboardPage,
  }))
);
export const CondominiumResidentsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumResidentsPage,
  }))
);
export const ResidentsTrashPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.ResidentsTrashPage,
  }))
);
export const CondominiumCollaboratorsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumCollaboratorsPage,
  }))
);
export const CreateCollaboratorPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateCollaboratorPage,
  }))
);
export const EditCollaboratorPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditCollaboratorPage,
  }))
);
export const CondominiumUnitsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumUnitsPage,
  }))
);
export const RecurringInvoicesPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.RecurringInvoicesPage,
  }))
);
export const CondominiumPaymentsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumPaymentsPage,
  }))
);
export const CondominiumBillingPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumBillingPage,
  }))
);
export const CondominiumSaleItemsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumSaleItemsPage,
  }))
);
export const CreateSaleItemPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateSaleItemPage,
  }))
);
export const CreateBillingPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateBillingPage,
  }))
);
export const EditBillingPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditBillingPage,
  }))
);
export const CondominiumMaintenancePage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumMaintenancePage,
  }))
);
export const CreateMaintenanceRequestPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateMaintenanceRequestPage,
  }))
);
export const CondominiumSettingsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumSettingsPage,
  }))
);
export const CondominiumPlansPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumPlansPage,
  }))
);
export const CondominiumAdministrationPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumAdministrationPage,
  }))
);
export const CreateResidentPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateResidentPage,
  }))
);
export const EditResidentPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditResidentPage,
  }))
);
export const CreateAdministrationPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateAdministrationPage,
  }))
);
export const EditAdministrationPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditAdministrationPage,
  }))
);
export const CreateAdministratorPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateAdministratorPage,
  }))
);
export const EditAdministratorPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditAdministratorPage,
  }))
);
export const PaymentCheckoutPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.PaymentCheckoutPage,
  }))
);
export const InvoiceTemplatesPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.InvoiceTemplatesPage,
  }))
);
export const CondominiumsPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CondominiumsPage,
  }))
);
export const CreateCondominiumPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateCondominiumPage,
  }))
);
export const EditCondominiumPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditCondominiumPage,
  }))
);
export const ProvidersPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.ProvidersPage,
  }))
);
export const CreateProviderPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.CreateProviderPage,
  }))
);
export const EditProviderPage = React.lazy(() =>
  import('@/features/condominium').then(m => ({
    default: m.EditProviderPage,
  }))
);

// Lazy loading de páginas de admin
export const AdminDashboardPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AdminDashboardPage }))
);
export const TenantsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.TenantsPage }))
);
export const TenantsTrashPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.TenantsTrashPage }))
);
export const CreateTenantPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.CreateTenantPage }))
);
export const EditTenantPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.EditTenantPage }))
);
export const TenantUsersPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.TenantUsersPage }))
);
export const UsersManagementPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.UsersManagementPage }))
);
export const VerificationPendingPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.VerificationPendingPage }))
);
export const UsersAdminActionsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.UsersAdminActionsPage }))
);
export const AdminSettingsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AdminSettingsPage }))
);
export const PlansPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.PlansPage }))
);
export const CreatePlanPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.CreatePlanPage }))
);
export const EditPlanPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.EditPlanPage }))
);
export const IntegrationsPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.IntegrationsPage }))
);
export const BillingPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.BillingPage }))
);
export const CreateInvoicePage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.CreateInvoicePage }))
);
export const AutomaticInvoicesPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.AutomaticInvoicesPage }))
);
export const ScheduledInvoicesPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.ScheduledInvoicesPage }))
);
export const InvoiceGenerationTestingPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.InvoiceGenerationTestingPage }))
);
export const SubscriptionPoliciesPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.SubscriptionPoliciesPage }))
);
export const SystemPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.SystemPage }))
);
export const CreateSubscriptionPolicyPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.CreateSubscriptionPolicyPage }))
);
export const EditPolicyPage = React.lazy(() =>
  import('@/features/admin').then(m => ({ default: m.EditPolicyPage }))
);

// Financial module — Invoice Wizard + Platform Billing
export const InvoiceWizardPage = React.lazy(() =>
  import('@/features/financial').then(m => ({ default: m.InvoiceWizardPage }))
);
export const PlatformBillingPage = React.lazy(() =>
  import('@/features/financial').then(m => ({ default: m.PlatformBillingPage }))
);

// Lazy loading de páginas de módulos
export const ModulesPage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({ default: m.ModulesPage }))
);
export const CreateModulePage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({
    default: m.CreateModulePage,
  }))
);
export const EditModulePage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({ default: m.EditModulePage }))
);
export const ModulePermissionsPage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({
    default: m.ModulePermissionsPage,
  }))
);
export const CategoriesPage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({
    default: m.CategoriesPage,
  }))
);

export const CategoriesTablePage = React.lazy(() =>
  import('@/features/admin/modules').then(m => ({
    default: m.CategoriesTablePage,
  }))
);

// Lazy loading del módulo WebPage — REEMPLAZADO por SoulBuilder.
// Todas las rutas /admin/webpage/* y /agency/webpage/* redirigen al editor SoulBuilder.
// Mantenemos 8 exports con el mismo nombre original para no romper rutas;
// todos apuntan al mismo componente SoulBuilderRedirect (aliased).
const SoulBuilderRedirectLazy = React.lazy(() =>
  import('@/shared/components/SoulBuilderRedirect').then(m => ({ default: m.SoulBuilderRedirect }))
);
export const WebpageDashboardPage = SoulBuilderRedirectLazy;
export const WebpageListPage = SoulBuilderRedirectLazy;
// F1.6 fusion (2026-06-06) — editor nativo InboMarket usando @soulbuilder/canvas
// + @soulbuilder/blocks-core via Vite alias. Reemplaza el redirect cross-app
// que mandaba a la Next.js standalone (la cual sera desmontada en F4).
export const WebpageBuilderPage = React.lazy(() =>
  import('@/features/builder/pages/BuilderEditorPage').then(m => ({ default: m.BuilderEditorPage }))
);
export const WebpageEditPage = SoulBuilderRedirectLazy;
export const WebpagePreviewPage = SoulBuilderRedirectLazy;
export const WebpageTemplateGallery = SoulBuilderRedirectLazy;
export const WebpageTemplatePreviewPage = SoulBuilderRedirectLazy;
export const WebpageTemplateEditPage = SoulBuilderRedirectLazy;

// Lazy loading de páginas del módulo Contactos
export const ContactsPage = React.lazy(() =>
  import('@/features/contacts').then(m => ({ default: m.ContactsPage }))
);

// Lazy loading de páginas del módulo Inbox
export const InboxPage = React.lazy(() =>
  import('@/features/inbox').then(m => ({ default: m.InboxPage }))
);

// Ola 2 GHL #5 — Mi Agenda (vista lista de citas).
export const AgendaPage = React.lazy(() =>
  import('@/features/agenda').then(m => ({ default: m.AgendaPage }))
);

// Ola 2 GHL #6 — Campañas de Email (marketing a segmentos).
export const EmailCampaignsPage = React.lazy(() =>
  import('@/features/email-campaigns').then(m => ({ default: m.EmailCampaignsPage }))
);

// GH#1631 — editor de una campaña de email (diseño / HTML / texto plano).
export const EditorCampanaEmailPage = React.lazy(() =>
  import('@/features/email-campaigns').then(m => ({ default: m.EditorCampanaEmailPage }))
);

// GHL 2.8 — detalle de una campaña enviada (KPIs, actividad, destinatarios).
export const DetalleCampanaEmailPage = React.lazy(() =>
  import('@/features/email-campaigns').then(m => ({ default: m.DetalleCampanaEmailPage }))
);

// Ola 2 GHL #8 — Secuencias de pipeline (drip automation).
export const SequencesPage = React.lazy(() =>
  import('@/features/sequences').then(m => ({ default: m.SequencesPage }))
);

// Ola 2 GHL #9 — Enlaces de pago (PayPal.me).
export const PaymentLinksPage = React.lazy(() =>
  import('@/features/payment-links').then(m => ({ default: m.PaymentLinksPage }))
);

// #874 — Calendarios de reserva (admin) + página pública de booking (Calendly).
export const CalendarSettingsPage = React.lazy(() =>
  import('@/features/calendar').then(m => ({ default: m.CalendarSettingsPage }))
);
export const PublicBookingPage = React.lazy(() =>
  import('@/features/calendar').then(m => ({ default: m.PublicBookingPage }))
);

// #873 — Widget de chat embebible (config + snippet).
export const WidgetEmbedPage = React.lazy(() =>
  import('@/features/widget').then(m => ({ default: m.WidgetEmbedPage }))
);

// Ola 2 GHL #11 — Config del Asistente IA (self-service).
export const AiSettingsPage = React.lazy(() =>
  import('@/features/ai-settings').then(m => ({ default: m.AiSettingsPage }))
);

// Lazy loading de páginas de error
export const NotFoundPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.NotFoundPage }))
);
export const UnauthorizedPage = React.lazy(() =>
  import('@/features/public').then(m => ({ default: m.UnauthorizedPage }))
);

// Lazy loading de páginas del Builder (E4)
export const BuilderOverviewPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderOverviewPage }))
);
export const BuilderTemplatePage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderTemplatePage }))
);
export const BuilderCustomizePage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderCustomizePage }))
);
export const BuilderPreviewPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderPreviewPage }))
);
export const BuilderPublishPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderPublishPage }))
);
export const BuilderAnalyticsPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderAnalyticsPage }))
);
export const BuilderDomainPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderDomainPage }))
);
// G1 + G2 (2026-06-07) — workspace surfaces port desde SoulBuilder
export const BuilderBrandKitsPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderBrandKitsPage }))
);
export const BuilderTemplatesLibraryPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderTemplatesLibraryPage }))
);
// Fase 4 (2026-06-13) — administración de standards
export const BuilderStandardsPage = React.lazy(() =>
  import('@/features/builder').then(m => ({ default: m.BuilderStandardsPage }))
);

// Lazy loading de páginas del módulo Email Templates
export const EmailTemplatesPage = React.lazy(() =>
  import('@/features/email-templates').then(m => ({ default: m.EmailTemplatesPage }))
);

// Lazy loading de páginas del módulo Templates (Unified)
export const TemplatesPage = React.lazy(() =>
  import('@/features/templates').then(m => ({ default: m.TemplatesPage }))
);

// Lazy loading de páginas del módulo API Keys
export const ApiKeysPage = React.lazy(() =>
  import('@/features/api-keys').then(m => ({ default: m.ApiKeysPage }))
);

// Lazy loading del modulo ERP (Sprint 8 — IRIS lane)
// ERP-8.1: dashboard ejecutivo (Journey Paul)
export const ErpExecutiveDashboardPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.ErpExecutiveDashboardPage }))
);
// ERP-8.2: journey contadora cierre mensual (asientos + reportes DGII)
export const JournalEntriesListPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.JournalEntriesListPage }))
);
export const JournalEntryDetailPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.JournalEntryDetailPage }))
);
export const JournalEntryNewPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.JournalEntryNewPage }))
);
export const DgiiReport606Page = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.DgiiReport606Page }))
);
export const DgiiReport607Page = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.DgiiReport607Page }))
);
export const DgiiReport608Page = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.DgiiReport608Page }))
);
// ERP-8.3: journey contadora dia normal (cola e-CF + detail)
export const EcfQueueListPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.EcfQueueListPage }))
);
export const EcfDetailPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.EcfDetailPage }))
);
// ERP-8.4: journey contadora trimestral (P12 + NCF + plan contable)
export const P12CertificatesPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.P12CertificatesPage }))
);
export const NcfRangesPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.NcfRangesPage }))
);
export const AccountsTreePage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.AccountsTreePage }))
);
// ERP-8.6: journey agente (mis comisiones)
export const AgenteCommissionsPage = React.lazy(() =>
  import('@/features/erp').then(m => ({ default: m.AgenteCommissionsPage }))
);

// Task #672-1 — Sprint 6 Network Console UI (page 1/6 dashboard)
export const NetworkDashboardPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkDashboardPage }))
);

// Task #672-2 — Sprint 6 Network Console UI (page 2/6 members + invitations)
export const NetworkMembersPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkMembersPage }))
);
export const NetworkInviteAcceptPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkInviteAcceptPage }))
);

// Task #672-3 — Sprint 6 Network Console UI (page 3/6 lead routing)
export const NetworkLeadRoutingPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkLeadRoutingPage }))
);

// Task #672-4 — Sprint 6 Network Console UI (page 4/6 regalías operational view)
export const NetworkRegaliasPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkRegaliasPage }))
);

// Task #672-5 — Sprint 6 Network Console UI (page 5/6 reports)
export const NetworkReportsPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkReportsPage }))
);

// Task #672-6 — Sprint 6 Network Console UI (page 6/6 settings — owner config)
export const NetworkSettingsPage = React.lazy(() =>
  import('@/features/network').then(m => ({ default: m.NetworkSettingsPage }))
);

// Task #677 — MLS connections UI (Sprint 8 GAP fill — field mapping + sandbox)
export const MlsConnectionsPage = React.lazy(() =>
  import('@/features/mls').then(m => ({ default: m.MlsConnectionsPage }))
);
export const MlsConnectionDetailPage = React.lazy(() =>
  import('@/features/mls').then(m => ({ default: m.MlsConnectionDetailPage }))
);

// PB0-CAT4-52 (Multi-Site) — sites management page (tenant-scoped CRUD + DNS)
export const SitesManagementPage = React.lazy(() =>
  import('@/features/multi-site').then(m => ({ default: m.SitesManagementPage }))
);

// Trust Badge — verification request flow + admin review
// AGENCY_ADMIN: request verification (/agencia/trust-badge/request)
// SUPER_ADMIN: review/approve/reject (/admin/trust-badge/review)
export const TrustBadgeRequestVerificationPage = React.lazy(() =>
  import('@/features/trust-badge').then(m => ({ default: m.RequestVerificationPage }))
);
export const TrustBadgeAdminReviewPage = React.lazy(() =>
  import('@/features/trust-badge').then(m => ({ default: m.AdminReviewPage }))
);

// #710 PB0-CAT4-55 MetaBroker IA — floating widget preview (public, no auth)
// FloatingWidgetPage embeds the marketplace bubble version of MetaBroker.
export const MetaBrokerFloatingWidgetPage = React.lazy(() =>
  import('@/features/metabroker').then(m => ({ default: m.FloatingWidgetPage }))
);

// Featured Listings — AGENCY_ADMIN promotion management
// MyActivePromotionsPage: list active promotions (/agencia/featured-listings/my-active)
// BuyPromotionPage: purchase new promotion (/agencia/featured-listings/buy-promotion)
export const FeaturedListingsMyActivePage = React.lazy(() =>
  import('@/features/featured-listings').then(m => ({ default: m.MyActivePromotionsPage }))
);
export const FeaturedListingsBuyPromotionPage = React.lazy(() =>
  import('@/features/featured-listings').then(m => ({ default: m.BuyPromotionPage }))
);

// Mortgage — public calculator widget preview page (embeddable on property pages)
// Public, no auth — wraps MortgageCalculator + capture lead flow.
export const MortgageCalculatorWidgetPage = React.lazy(() =>
  import('@/features/mortgage').then(m => ({ default: m.CalculatorWidgetPage }))
);
